import { create } from 'zustand';
import type { AppState, SaveGame, GoldenWalnut, AppView } from '../types';
import {
  apiLogin,
  apiRegister,
  listSaves as apiListSaves,
  createSave as apiCreateSave,
  updateSave as apiUpdateSave,
  deleteSave as apiDeleteSave,
  getWalnutsForSave,
  addWalnutToSave,
  deleteWalnutEntry,
  type ApiUser,
  type SaveDocument,
  type WalnutEntry,
} from '../lib/api';

interface AppStore extends AppState {
  currentView: AppView;
  setCurrentView: (view: AppView) => void;
  setCurrentSave: (id: number | null) => void;
  setSaves: (saves: SaveGame[]) => void;
  addSave: (save: SaveGame) => Promise<void>;
  updateSave: (save: SaveGame) => Promise<void>;
  deleteSave: (id: number) => Promise<void>;
  setWalnuts: (walnuts: GoldenWalnut[]) => void;
  updateWalnutCount: (saveId: number, walnutId: string, count: number) => Promise<void>;
  loadData: () => Promise<void>;
  refreshSaves: () => Promise<void>;
  refreshWalnuts: () => Promise<void>;
  forceRefreshWalnuts: () => Promise<void>;
  refreshWalnutEntries: (saveId: number) => Promise<void>;
  user: ApiUser | null;
  authLoading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  signUp: (email: string, password: string, username: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  // New state for walnut entries
  walnutEntries: Record<number, WalnutEntry[]>; // saveId -> walnut entries
  // Loading states to prevent unnecessary reloads
  dataLoading: boolean;
  walnutsLoaded: boolean;
}

// Helper to extract user info from token or create minimal user object
function createUserFromToken(email: string): ApiUser {
  // Since the API only returns a token, we'll create a minimal user object
  // In a real app, you might decode the JWT to get user info or make a separate call
  return {
    id: email, // Use email as ID for now
    email: email,
  };
}

export const useAppStore = create<AppStore>((set, get) => ({
  // Initial state
  user: null,
  authLoading: false,
  authError: null,
  saves: [],
  currentSaveId: null,
  walnuts: [],
  currentView: 'selectSave',
  walnutEntries: {},
  dataLoading: false, // Start with false to allow initial load
  walnutsLoaded: false, // Initialize walnutsLoaded state

  // Auth methods
  signIn: async (email, password) => {
    set({ authLoading: true, authError: null });
    try {
      const response = await apiLogin(email, password);
      const { token } = response;
      
      if (token) {
        localStorage.setItem('token', token);
        const user = createUserFromToken(email);
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, authLoading: false });
        
        // Load data after successful login
        await get().loadData();
        return true;
      } else {
        throw new Error('No token received from login');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ authError: errorMessage, authLoading: false });
      return false;
    }
  },

  signUp: async (email, password, username) => {
    set({ authLoading: true, authError: null });
    try {
      const response = await apiRegister(email, password, username);
      const { token } = response;
      
      if (token) {
        localStorage.setItem('token', token);
        const user = createUserFromToken(email);
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, authLoading: false });
        
        // Load data after successful registration
        await get().loadData();
        return true;
      } else {
        throw new Error('No token received from registration');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      set({ authError: errorMessage, authLoading: false });
      return false;
    }
  },

  signOut: async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentSaveId');
    set({
      user: null,
      saves: [],
      currentSaveId: null,
      currentView: 'selectSave',
      walnuts: [],
      walnutEntries: {},
      dataLoading: true, // Reset loading state on sign out
      walnutsLoaded: false, // Reset walnutsLoaded state on sign out
    });
  },

  // View management
  setCurrentView: (view) => set({ currentView: view }),
  
  setCurrentSave: (id) => {
    if (id) {
      localStorage.setItem('currentSaveId', String(id));
    } else {
      localStorage.removeItem('currentSaveId');
    }
    set({
      currentSaveId: id,
      currentView: id ? 'walnutList' : 'selectSave',
    });
  },

  // Save management - all operations now go through backend
  setSaves: (saves) => set({ saves }),
  
  addSave: async (save) => {
    const { user } = get();
    if (!user) {
      throw new Error('User must be authenticated to create saves');
    }

    try {
      console.log('Creating save:', { name: save.name, userId: user.id });
      
      const created = await apiCreateSave({
        name: save.name,
      });

      console.log('Save created successfully:', created);

      const newSave: SaveGame = {
        id: created.id,
        name: save.name,
        createdAt: save.createdAt,
        walnuts: save.walnuts,
      };

      set((state) => ({ saves: [...state.saves, newSave] }));
      
      // Set as current save if it's the first one
      if (get().saves.length === 1) {
        get().setCurrentSave(newSave.id);
      }
    } catch (error) {
      console.error('Failed to create save:', {
        saveName: save.name,
        userId: user.id,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  },

  updateSave: async (save) => {
    const { user } = get();
    if (!user) {
      throw new Error('User must be authenticated to update saves');
    }

    try {
      console.log('Updating save:', { id: save.id, name: save.name });
      
      await apiUpdateSave(save.id, {
        name: save.name,
      });

      console.log('Save updated successfully:', save.id);

      set((state) => ({
        saves: state.saves.map((s) => s.id === save.id ? save : s),
      }));
    } catch (error) {
      console.error('Failed to update save:', {
        saveId: save.id,
        saveName: save.name,
        userId: user.id,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  },

  deleteSave: async (id) => {
    const { user } = get();
    if (!user) {
      throw new Error('User must be authenticated to delete saves');
    }

    try {
      console.log('Deleting save:', { id, userId: user.id });
      
      await apiDeleteSave(id);
      
      console.log('Save deleted successfully:', id);

      set((state) => ({
        saves: state.saves.filter((s) => s.id !== id),
        currentSaveId: state.currentSaveId === id ? null : state.currentSaveId,
        walnutEntries: Object.fromEntries(
          Object.entries(state.walnutEntries).filter(([saveId]) => Number(saveId) !== id)
        ),
      }));

      // If we deleted the current save, clear it from localStorage
      if (get().currentSaveId === null) {
        localStorage.removeItem('currentSaveId');
      }
    } catch (error) {
      console.error('Failed to delete save:', {
        saveId: id,
        userId: user.id,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  },

  // Walnut management - using separate walnut endpoints
  setWalnuts: (walnuts) => set({ walnuts }),
  
  updateWalnutCount: async (saveId, walnutId, count) => {
    const { user } = get();
    if (!user) {
      throw new Error('User must be authenticated to update walnut counts');
    }

    try {
      console.log('Updating walnut count:', { saveId, walnutId, count, userId: user.id });
      
      const currentEntries = get().walnutEntries[saveId] || [];
      const currentCount = currentEntries.filter(entry => entry.walnut_id === walnutId).length;

      console.log('Current walnut entries:', {
        saveId,
        walnutId,
        currentCount,
        totalEntries: currentEntries.length,
        entries: currentEntries.filter(entry => entry.walnut_id === walnutId),
      });

      if (count > currentCount) {
        // Add walnuts
        const walnutsToAdd = count - currentCount;
        console.log(`Adding ${walnutsToAdd} walnuts to save ${saveId}`);
        
        for (let i = 0; i < walnutsToAdd; i++) {
          try {
            const newEntry = await addWalnutToSave(saveId, walnutId);
            console.log(`Added walnut entry ${i + 1}/${walnutsToAdd}:`, newEntry);
          } catch (addError) {
            console.error(`Failed to add walnut ${i + 1}/${walnutsToAdd}:`, {
              saveId,
              walnutId,
              attempt: i + 1,
              error: addError instanceof Error ? addError.message : String(addError),
            });
            throw addError;
          }
        }
      } else if (count < currentCount) {
        // Remove walnuts
        const walnutsToRemove = currentCount - count;
        const entriesToRemove = currentEntries
          .filter(entry => entry.walnut_id === walnutId)
          .slice(0, walnutsToRemove);
        
        console.log(`Removing ${walnutsToRemove} walnuts from save ${saveId}:`, entriesToRemove);
        
        for (const entry of entriesToRemove) {
          try {
            await deleteWalnutEntry(saveId, entry.id);
            console.log(`Deleted walnut entry:`, entry);
          } catch (deleteError) {
            console.error(`Failed to delete walnut entry:`, {
              saveId,
              entryId: entry.id,
              walnutId: entry.walnut_id,
              error: deleteError instanceof Error ? deleteError.message : String(deleteError),
            });
            throw deleteError;
          }
        }
      } else {
        console.log('No change needed for walnut count');
      }

      // Refresh walnut entries for this save
      console.log('Refreshing walnut entries for save:', saveId);
      await get().refreshWalnutEntries(saveId);
      
      console.log('Walnut count update completed successfully');
    } catch (error) {
      console.error('Failed to update walnut count:', {
        saveId,
        walnutId,
        targetCount: count,
        userId: user.id,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  },

  // Data loading methods
  loadData: async () => {
    const { dataLoading, walnutsLoaded } = get();
    
    // Prevent multiple simultaneous data loads
    if (dataLoading) {
      console.log('Data loading already in progress, skipping...');
      return;
    }
    
    set({ dataLoading: true });
    
    try {
      // Load user from localStorage
      const token = localStorage.getItem('token');
      let user: ApiUser | null = null;
      const cachedUser = localStorage.getItem('user');
      
      console.log('Loading data - token exists:', !!token, 'cached user exists:', !!cachedUser);
      
      if (token && cachedUser) {
        try {
          user = JSON.parse(cachedUser);
          console.log('Restored user from localStorage:', user);
          set({ user });
        } catch (parseError) {
          console.error('Failed to parse cached user:', parseError);
          set({ user: null });
        }
      } else if (token && !cachedUser) {
        // Token exists but no cached user - this shouldn't happen, but let's handle it
        console.warn('Token exists but no cached user found');
        set({ user: null });
      } else {
        console.log('No token found, user not authenticated');
        set({ user: null });
      }

      // Load current save ID from localStorage
      const currentSaveId = localStorage.getItem('currentSaveId');
      if (currentSaveId) {
        console.log('Restored current save ID from localStorage:', currentSaveId);
        set({ currentSaveId: Number(currentSaveId), currentView: 'walnutList' });
      }

      // Load saves from backend if authenticated
      if (user) {
        console.log('User is authenticated, loading saves...');
        await get().refreshSaves();
      } else {
        console.log('User not authenticated, skipping saves load');
      }

      // Only load walnuts if not already loaded
      if (!walnutsLoaded) {
        console.log('Loading walnuts...');
        await get().refreshWalnuts();
      } else {
        console.log('Walnuts already loaded, skipping...');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      set({ dataLoading: false });
    }
  },

  refreshSaves: async () => {
    const { user } = get();
    if (!user) return;

    try {
      console.log('Loading saves for user:', user.id);
      
      const docs: SaveDocument[] = await apiListSaves();
      console.log('Saves loaded from API:', docs);
      
      const saves: SaveGame[] = docs.map((doc) => ({
        id: doc.id,
        name: doc.name,
        createdAt: doc.created_at,
        walnuts: {}, // Will be populated from walnut entries
      }));
      
      console.log('Processed saves:', saves);
      set({ saves });
      
      // Load walnut entries for each save
      console.log('Loading walnut entries for each save...');
      for (const save of saves) {
        try {
          await get().refreshWalnutEntries(save.id);
        } catch (walnutError) {
          console.error(`Failed to load walnut entries for save ${save.id}:`, {
            saveId: save.id,
            saveName: save.name,
            error: walnutError instanceof Error ? walnutError.message : String(walnutError),
          });
          // Continue loading other saves even if one fails
        }
      }
      
      // If we have a current save ID but it's not in the loaded saves, clear it
      const currentSaveId = get().currentSaveId;
      if (currentSaveId && !saves.find(s => s.id === currentSaveId)) {
        console.log('Current save not found in loaded saves, clearing selection');
        localStorage.removeItem('currentSaveId');
        set({ currentSaveId: null, currentView: 'selectSave' });
      }
      
      // If no current save is selected but we have saves, select the most recent
      if (!currentSaveId && saves.length > 0) {
        const mostRecent = saves.reduce((latest: SaveGame, current: SaveGame) =>
          new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest
        );
        console.log('Auto-selecting most recent save:', mostRecent);
        get().setCurrentSave(mostRecent.id);
      }
      
      console.log('Saves refresh completed successfully');
    } catch (error) {
      console.error('Failed to load saves:', {
        userId: user.id,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
    }
  },

  refreshWalnutEntries: async (saveId: number) => {
    try {
      console.log(`Loading walnut entries for save ${saveId}`);
      
      const entries = await getWalnutsForSave(saveId);
      console.log(`Walnut entries for save ${saveId}:`, entries);
      
      set((state) => ({
        walnutEntries: {
          ...state.walnutEntries,
          [saveId]: entries,
        },
        // Update the walnuts count in the save
        saves: state.saves.map((save) => {
          if (save.id === saveId) {
            const walnutCounts: Record<string, number> = {};
            entries.forEach(entry => {
              walnutCounts[entry.walnut_id] = (walnutCounts[entry.walnut_id] || 0) + 1;
            });
            console.log(`Updated walnut counts for save ${saveId}:`, walnutCounts);
            return { ...save, walnuts: walnutCounts };
          }
          return save;
        }),
      }));
      
      console.log(`Walnut entries refresh completed for save ${saveId}`);
    } catch (error) {
      console.error(`Failed to load walnut entries for save ${saveId}:`, {
        saveId,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  },

  refreshWalnuts: async () => {
    const { walnutsLoaded } = get();
    
    // Prevent reloading if already loaded
    if (walnutsLoaded) {
      console.log('Walnuts already loaded, skipping refresh...');
      return;
    }
    
    // For now, always use local JSON to prevent flickering issues
    // TODO: Fix API data consistency before enabling API loading
    console.log('Using local JSON data to prevent flickering issues');
    
    try {
      const response = await fetch('/golden_walnuts_scraped.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch local JSON: ${response.status}`);
      }
      
      const walnuts = await response.json();
      console.log('Loaded walnuts from local JSON:', walnuts.length, 'entries');
      
      if (!walnuts || walnuts.length === 0) {
        throw new Error('Local JSON is empty');
      }
      
      set({ walnuts, walnutsLoaded: true });
      console.log('Successfully loaded walnuts from local JSON');
    } catch (error) {
      console.error('Failed to load walnuts from local JSON:', error);
      set({ walnuts: [], walnutsLoaded: true });
    }
    
    /* 
    // API loading code (disabled to prevent flickering)
    try {
      console.log('Loading golden walnut types from API');
      
      // Use the /golden-walnuts endpoint as per OpenAPI spec
      const walnutTypes = await listGoldenWalnutTypes();
      console.log('Golden walnut types loaded from API:', walnutTypes);
      
      // Check if API returned valid data
      if (!walnutTypes || walnutTypes.length === 0) {
        throw new Error('API returned empty walnut data');
      }
      
      // Convert API format to our internal format
      const walnuts: GoldenWalnut[] = walnutTypes.map(type => ({
        id: type.id,
        quantity: type.quantity,
        type_of_discovery: type.type_of_discovery,
        how_to_find: type.how_to_find,
        image: type.image,
        location: type.location,
      }));
      
      set({ walnuts, walnutsLoaded: true });
      console.log('Golden walnuts refresh completed successfully from API');
    } catch (error) {
      console.error('Failed to load golden walnut types from API:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // Fallback to local JSON if API fails
      console.log('Falling back to local JSON data');
      try {
        const response = await fetch('/golden_walnuts_scraped.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch local JSON: ${response.status}`);
        }
        
        const walnuts = await response.json();
        console.log('Loaded walnuts from local JSON fallback:', walnuts.length, 'entries');
        
        if (!walnuts || walnuts.length === 0) {
          throw new Error('Local JSON is empty');
        }
        
        set({ walnuts, walnutsLoaded: true });
        console.log('Successfully loaded walnuts from local JSON fallback');
      } catch (fallbackError) {
        console.error('Failed to load walnuts from local JSON:', fallbackError);
        set({ walnuts: [], walnutsLoaded: true });
      }
    }
    */
  },

  // Force refresh walnuts (for manual refresh)
  forceRefreshWalnuts: async () => {
    console.log('Force refreshing walnuts...');
    set({ walnutsLoaded: false }); // Reset flag to allow refresh
    await get().refreshWalnuts();
  },
}));
